import * as React from "react";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { RemoteLinkSite } from "./sitesPageStore/sitesPageStore";

/**
 * Helpers for the Actuate-site drop-down in the Immix Remotelink dialog.
 *
 * Options are groups from /group/?group_type=remote_link. For a single-site
 * group the backend also sends site_id / site_name, which we use to:
 *  - label the option "Name (#123)" so the site number is visible,
 *  - let the search match the site number and the site's own name (the
 *    group name does not always contain it),
 *  - after a pick, link to /sites/aboutsite/123 beside Save so the user can
 *    confirm it is the right site before saving the mapping. (Options
 *    themselves carry no link: it crowded the row and competed with the
 *    click that selects the option.)
 */

/**
 * Remote Link maps an Immix *site* to an Actuate *site*, so the dropdown only
 * offers single-site groups. site_count is on both the old and new backend;
 * site_id only on the new one, so site_count is the predicate.
 */
export const isSingleSiteOption = (option: RemoteLinkSite): boolean =>
  option.site_count === 1;

export const remoteLinkOptionLabel = (option: RemoteLinkSite): string =>
  option.site_id != null ? `${option.name} (#${option.site_id})` : option.name;

export const siteAboutPath = (siteId: number): string =>
  `/sites/aboutsite/${siteId}`;

export const filterRemoteLinkOptions = createFilterOptions<RemoteLinkSite>({
  ignoreCase: true,
  ignoreAccents: true,
  trim: true,
  stringify: (option) =>
    [
      option.name,
      option.site_name ?? "",
      option.site_id != null ? `#${option.site_id} ${option.site_id}` : "",
    ].join(" "),
});

/** Opens the site page in a new tab without selecting/closing the option. */
export const RemoteLinkSiteLink = ({
  siteId,
  label,
}: {
  siteId: number;
  label: string;
}) => (
  <a
    href={siteAboutPath(siteId)}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={`Open site ${siteId} in a new tab`}
    className="shrink-0 font-mulish text-[13px] text-actuate-orange underline hover:text-[#E67300]"
    onMouseDown={(event) => event.stopPropagation()}
    onClick={(event) => event.stopPropagation()}
  >
    {label}
  </a>
);
